/**
 * Starting equipment: the armor and weapon a player picks for a new
 * character during creation. The armor doubles as the character's outfit
 * (see `EquipmentPiece.outfitParts`). The server checks a pick against these
 * lists and equips its catalog definition; anything else is refused.
 */
import type { Equipment } from './types/game';

/** One starting-equipment choice: the database item it equips and the catalog model (icon, 3D parts) behind it. */
export interface StartingEquipmentOption {
    /** The `items` row (database) a pick equips. */
    readonly itemDefinitionId: string;
    /** Its `items.json` entry and `item-models/` folder (see `resolveItemModelId`). */
    readonly modelId: string;
}

/** Starting armor: plain clothing, each its own look. The first is the default. */
export const STARTING_ARMOR_OPTIONS: readonly StartingEquipmentOption[] = [
    { itemDefinitionId: '0896becd-eb8c-45d1-9230-a87988c65ca0', modelId: 'peasant-outfit' },
    { itemDefinitionId: '2a5189ad-02fc-4397-8a76-2607dd7441cf', modelId: 'ranger-outfit' },
];

/** Starting weapons. The first is the default. */
export const STARTING_WEAPON_OPTIONS: readonly StartingEquipmentOption[] = [
    { itemDefinitionId: 'shortsword', modelId: 'shortsword' },
    // A bow: a start at range, for those who would rather not be within reach.
    { itemDefinitionId: 'shortbow', modelId: 'shortbow' },
];

export const DEFAULT_STARTING_ARMOR = STARTING_ARMOR_OPTIONS[0]!;
export const DEFAULT_STARTING_WEAPON = STARTING_WEAPON_OPTIONS[0]!;

/** The option a pick names, or undefined when it isn't one of `options`. */
export function findStartingEquipmentOption(
    options: readonly StartingEquipmentOption[],
    itemDefinitionId: string,
): StartingEquipmentOption | undefined {
    return options.find((option) => option.itemDefinitionId === itemDefinitionId) ?? undefined;
}

/**
 * `GET /api/starting-equipment`: each starting option's catalog copy, as the
 * server equips it at creation, in option order. Options missing from the
 * catalog are left out.
 */
export interface StartingEquipmentCatalog {
    readonly armor: readonly Equipment[];
    readonly weapons: readonly Equipment[];
}
